/**
 * Facebook Page - Videos Webhook Types
 *
 * These types represent the webhook event structure for video encoding events.
 * Triggered when there are changes to the encoding status of a video on a page.
 *
 * @see https://developers.facebook.com/docs/graph-api/webhooks/reference/page/#videos
 */

import { PageWebhookPayload } from './base-types';

/**
 * Enumeration of video encoding statuses
 */
export enum VideoStatus {
  /** Video encoding is in progress */
  PROCESSING = 'processing',

  /** Video encoding is complete and ready */
  READY = 'ready',

  /** Video encoding failed */
  ERROR = 'error',
}

/**
 * Video status information
 */
export interface VideoStatusInfo {
  /** Current status of the video encoding */
  video_status: VideoStatus;
}

/**
 * Video event value data
 */
export interface VideoEventValue {
  /** The ID of the video */
  id: string;

  /** Status information of the video encoding */
  status: VideoStatusInfo;
}

/**
 * Main webhook event structure for videos
 *
 * This event is triggered when there are changes to a video's encoding status.
 * The webhook provides information about the video processing state.
 *
 * @example Video processing:
 * ```json
 * {
 *   "field": "videos",
 *   "value": {
 *     "id": "123456789",
 *     "status": {
 *       "video_status": "processing"
 *     }
 *   }
 * }
 * ```
 *
 * @example Video ready:
 * ```json
 * {
 *   "field": "videos",
 *   "value": {
 *     "id": "123456789",
 *     "status": {
 *       "video_status": "ready"
 *     }
 *   }
 * }
 * ```
 *
 * @example Video error:
 * ```json
 * {
 *   "field": "videos",
 *   "value": {
 *     "id": "123456789",
 *     "status": {
 *       "video_status": "error"
 *     }
 *   }
 * }
 * ```
 */
export interface VideoWebhookEvent {
  /** Name of the updated field */
  field: 'videos';

  /** The contents of the update */
  value: VideoEventValue;
}

/**
 * Complete webhook payload structure for video events
 */
export interface VideoWebhookPayload extends PageWebhookPayload<VideoWebhookEvent> {}

/**
 * Type guard to check if a webhook event is a video event
 *
 * @param event - The webhook event to check
 * @returns True if the event is a video event
 *
 * @example
 * ```typescript
 * if (isVideoEvent(event)) {
 *   console.log(`Video ${event.value.id} status: ${event.value.status.video_status}`);
 * }
 * ```
 */
export function isVideoEvent(event: any): event is VideoWebhookEvent {
  return event && typeof event === 'object' && event.field === 'videos';
}

/**
 * Type guard to check if a video is processing
 *
 * @param value - The video event value to check
 * @returns True if the video is being processed
 */
export function isProcessing(value: VideoEventValue): boolean {
  return value.status.video_status === VideoStatus.PROCESSING;
}

/**
 * Type guard to check if a video is ready
 *
 * @param value - The video event value to check
 * @returns True if the video encoding is complete
 */
export function isReady(value: VideoEventValue): boolean {
  return value.status.video_status === VideoStatus.READY;
}

/**
 * Type guard to check if a video encoding failed
 *
 * @param value - The video event value to check
 * @returns True if the video encoding failed
 */
export function hasError(value: VideoEventValue): boolean {
  return value.status.video_status === VideoStatus.ERROR;
}

/**
 * Utility type for extracting just the video event data
 */
export type VideoEventData = VideoEventValue;

/**
 * Processing context for video events
 */
export interface VideoProcessingContext {
  /** The page ID */
  pageId: string;

  /** When the event occurred */
  timestamp: number;

  /** Human-readable datetime for the event timestamp */
  eventDate: Date;

  /** The video ID */
  videoId: string;

  /** Current video status */
  status: VideoStatus;

  /** Whether the video is being processed */
  isProcessing: boolean;

  /** Whether the video is ready */
  isReady: boolean;

  /** Whether the video encoding failed */
  hasError: boolean;
}

/**
 * Helper function to extract processing context from a video event
 *
 * @param pageId - The page ID from the webhook entry
 * @param timestamp - The timestamp from the webhook entry
 * @param event - The video webhook event
 * @returns Simplified processing context
 *
 * @example
 * ```typescript
 * const context = extractVideoContext(entry.id, entry.time, event);
 * console.log(`Video ${context.videoId} is ${context.status}`);
 * if (context.isReady) {
 *   console.log('Video is ready to play!');
 * } else if (context.hasError) {
 *   console.log('Video encoding failed');
 * }
 * ```
 */
export function extractVideoContext(
  pageId: string,
  timestamp: number,
  event: VideoWebhookEvent
): VideoProcessingContext {
  const { value } = event;

  return {
    pageId,
    timestamp,
    eventDate: new Date(timestamp),
    videoId: value.id,
    status: value.status.video_status,
    isProcessing: isProcessing(value),
    isReady: isReady(value),
    hasError: hasError(value),
  };
}

/**
 * Helper function to determine if a status change is a transition to ready
 *
 * @param fromStatus - The previous status
 * @param toStatus - The new status
 * @returns True if this represents completing encoding
 *
 * @example
 * ```typescript
 * if (isCompletingEncoding(previousStatus, event.value.status.video_status)) {
 *   console.log('Video encoding just completed!');
 * }
 * ```
 */
export function isCompletingEncoding(fromStatus: VideoStatus | undefined, toStatus: VideoStatus): boolean {
  return fromStatus === VideoStatus.PROCESSING && toStatus === VideoStatus.READY;
}

/**
 * Helper function to determine if a status change is a transition to error
 *
 * @param fromStatus - The previous status
 * @param toStatus - The new status
 * @returns True if this represents encoding failure
 *
 * @example
 * ```typescript
 * if (isEncodingFailed(previousStatus, event.value.status.video_status)) {
 *   console.log('Video encoding failed');
 * }
 * ```
 */
export function isEncodingFailed(fromStatus: VideoStatus | undefined, toStatus: VideoStatus): boolean {
  return fromStatus === VideoStatus.PROCESSING && toStatus === VideoStatus.ERROR;
}

/**
 * Constants related to video events
 */
export const VIDEO_CONSTANTS = {
  /** Webhook field name */
  FIELD_NAME: 'videos' as const,

  /** All possible video statuses */
  STATUSES: Object.values(VideoStatus),
} as const;
